import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  nonInsurableApi,
  NON_INSURABLE_REASONS,
  type NonInsurablePeriodDto,
} from '../../api/nonInsurablePeriods';
import { workerContractsApi } from '../../api/workerContracts';
import { useIsMirror } from '../../hooks/useIsMirror';
import { useReportScreen } from '../../components/ScreenContext';
import LoadingSpinner from '../../components/LoadingSpinner';

const STATUS_STYLE: Record<string, string> = {
  Juodraštis: 'bg-amber-50 text-amber-700 border-amber-200',
  Patvirtintas: 'bg-green-50 text-green-700 border-green-200',
  Deklaruotas: 'bg-primary-50 text-primary-700 border-primary-200',
  Anuliuotas: 'bg-gray-100 text-gray-500 border-gray-200',
};

/**
 * D1 — Nedraudiminiai laikotarpiai (12-SD įrašai): sąrašas su statusais,
 * rankinis įvedimas, „Užpildyti iš tabelių" backfill. Periodai su
 * GeneratesSD12 klase atsiranda automatiškai išsaugojus tabelį. Tik
 * PATVIRTINTI patenka į 12-SD deklaraciją (Sodros deklaracijų puslapis).
 */
export default function NonInsurablePeriods() {
  const isMirror = useIsMirror();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const { data: periods, isLoading } = useQuery({
    queryKey: ['non-insurable-periods'],
    queryFn: () => nonInsurableApi.list(),
  });
  const { data: contracts } = useQuery({
    queryKey: ['worker-contracts'],
    queryFn: workerContractsApi.list,
    staleTime: 5 * 60 * 1000,
  });

  const reload = () => queryClient.invalidateQueries({ queryKey: ['non-insurable-periods'] });

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await reload();
    } catch (e: any) {
      setError(e.message || 'Nepavyko');
    } finally {
      setBusy(false);
    }
  };

  useReportScreen(
    `Nedraudiminiai laikotarpiai: ${periods?.length ?? 0}${isMirror ? ' (tik skaityti)' : ''}`,
    { recordCount: periods?.length ?? 0 },
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Nedraudiminiai laikotarpiai</h1>
          <p className="text-sm text-gray-500">
            12-SD įrašai — patvirtinti laikotarpiai patenka į 12-SD deklaraciją (Sodros deklaracijos → 12-SD).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/payroll"
            className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:border-gray-400"
          >
            ← Darbo užmokestis
          </Link>
          {!isMirror && (
            <>
              <button
                onClick={() => run(() => nonInsurableApi.backfill('2000-01-01', new Date().toISOString().slice(0, 10)))}
                disabled={busy}
                className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:border-gray-400 disabled:opacity-40"
                title="Atkuria automatinius įrašus iš visų išsaugotų tabelių (kartotina)."
              >
                Užpildyti iš tabelių
              </button>
              <button
                onClick={() => setShowForm(v => !v)}
                className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700"
              >
                {showForm ? 'Uždaryti' : '+ Laikotarpis'}
              </button>
            </>
          )}
        </div>
      </div>

      {isMirror && <p className="text-sm text-gray-500">Ši darbo sritis tik skaityti — laikotarpius kurkite Apskaita5 programoje.</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!isMirror && showForm && <AddForm contracts={contracts ?? []} onDone={async () => { setShowForm(false); await reload(); }} onError={setError} />}

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Darbuotojas</th>
                <th>Sutartis</th>
                <th>Nuo</th>
                <th>Iki</th>
                <th>Priežastis</th>
                <th>Būsena</th>
                <th>Šaltinis</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(periods ?? []).map(p => (
                <PeriodRow key={p.id} p={p} isMirror={isMirror} busy={busy} run={run} />
              ))}
              {(periods ?? []).length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-gray-500">
                    Įrašų nėra — sukurkite rankiniu būdu arba pažymėkite darbo laiko klasę „generuoja 12-SD" ir išsaugokite tabelį.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PeriodRow({
  p, isMirror, busy, run,
}: {
  p: NonInsurablePeriodDto;
  isMirror: boolean;
  busy: boolean;
  run: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  return (
    <tr>
      <td>{p.workerName}</td>
      <td>{p.contractSerial} {p.contractNumber}</td>
      <td>{p.dateFrom.slice(0, 10)}</td>
      <td>{p.dateTo.slice(0, 10)}</td>
      <td>{p.reasonCode} — {p.reasonText}</td>
      <td>
        <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full border ${STATUS_STYLE[p.status] ?? ''}`}>
          {p.status}
        </span>
      </td>
      <td className="text-xs text-gray-400">
        {p.source === 0 ? 'Rankinis' : p.source === 1 ? 'Tabelis' : 'Atkūrimas'}
      </td>
      <td>
        {!isMirror && busy !== null && p.status !== 'Deklaruotas' && p.status !== 'Anuliuotas' && (
          <span className="flex gap-1">
            {p.status === 'Juodraštis' && (
              <button onClick={() => run(() => nonInsurableApi.setStatus(p.id, 'approve'))}>Patvirtinti</button>
            )}
            {p.status === 'Patvirtintas' && (
              <>
                <button onClick={() => run(() => nonInsurableApi.setStatus(p.id, 'declare'))}>Deklaruotas</button>
                <button onClick={() => run(() => nonInsurableApi.setStatus(p.id, 'cancel'))}>Anuliuoti</button>
              </>
            )}
            {p.status !== 'Deklaruotas' && (
              <button onClick={() => run(() => nonInsurableApi.delete(p.id))}>Trinti</button>
            )}
          </span>
        )}
      </td>
    </tr>
  );
}

function AddForm({
  contracts, onDone, onError,
}: {
  contracts: { workerId: number; workerName: string; contractNumber: number; contractSerial: string }[];
  onDone: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [workerId, setWorkerId] = useState<number | null>(null);
  const [dateFrom, setDateFrom] = useState(() => new Date().toISOString().slice(0, 8) + '01');
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [reasonCode, setReasonCode] = useState('07');
  const [basis, setBasis] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!workerId) {
      onError('Pasirinkite darbuotoją.');
      return;
    }
    const contract = contracts.find(c => c.workerId === workerId);
    setSaving(true);
    onError('');
    try {
      await nonInsurableApi.save({
        workerId,
        contractNumber: contract?.contractNumber ?? 1,
        contractSerial: contract?.contractSerial ?? '',
        dateFrom,
        dateTo,
        reasonCode,
        basis: basis || null,
      });
      setBasis('');
      await onDone();
    } catch (e: any) {
      onError(e.message || 'Nepavyko išsaugoti');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ padding: '1rem', display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Darbuotojas</span>
        <select value={workerId ?? ''} onChange={e => setWorkerId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">— darbuotojas —</option>
          {contracts.map(c => (
            <option key={c.workerId} value={c.workerId}>{c.workerName}</option>
          ))}
        </select>
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Nuo</span>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Iki</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Priežastis</span>
        <select value={reasonCode} onChange={e => setReasonCode(e.target.value)}>
          {NON_INSURABLE_REASONS.map(r => (
            <option key={r.code} value={r.code}>{r.code} — {r.label}</option>
          ))}
        </select>
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Pagrindas</span>
        <input style={{ width: '14rem' }} value={basis} onChange={e => setBasis(e.target.value)} />
      </label>
      <button onClick={submit} disabled={saving}
        className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40">
        {saving ? '…' : 'Išsaugoti'}
      </button>
    </div>
  );
}
